/**
 * Статус партии-даты: «Активный» ↔ «Отмена». Открывается кликом по статусу в
 * строке партии «Планов». Спека `docs/specs/2026-11-04-plan-line-status-design.md`,
 * «Статус партии».
 *
 * Панель, а не поповер у чипа: строка партии живёт в виртуализированном списке
 * со своим `overflow` и `transform`, и выпадающее меню обрезалось бы краем
 * прокрутки либо уезжало вместе со строкой.
 *
 * «Отгружен» вручную не ставим — его ставит крон areserv; у такой партии
 * кнопки статуса в строке нет вовсе. Оплаченную папку не запираем (решение
 * владельца, как с QR) — только предупреждаем.
 */

import { useState } from "react";

import { SidePanel } from "@/components/layout/SidePanel";
import { ApiFailure, apiFetch } from "@/lib/api";
import type { PlanRow } from "@/lib/providers";
import { Badge } from "./Badge";
import { STATUS_TONE } from "./style";
import type { BatchStatusSaved } from "./types";

const КНОПКИ = [
  { status: 1, label: "Активный" },
  { status: 2, label: "Отмена" },
] as const;

export function PlanBatchPanel({
  plan,
  blocked,
  onClose,
  onSaved,
}: {
  plan: PlanRow;
  /** Почему смена статуса партии закрыта, словами; `null` — открыта. */
  blocked: string | null;
  onClose: () => void;
  onSaved: (result: BatchStatusSaved) => void;
}) {
  const [идёт, setИдёт] = useState(false);
  const [беда, setБеда] = useState<string | null>(null);
  const [итог, setИтог] = useState<string | null>(null);
  // Своя причина словами: текст `blocked` — про смену даты (там «CRM»), здесь не к месту.
  const причина =
    plan.status_id === 3
      ? "партию закрыл areserv — «Отгружен» ставит крон"
      : blocked
        ? "запись статуса партии сейчас закрыта"
        : null;

  const сменить = async (status: number) => {
    setИдёт(true);
    setБеда(null);
    setИтог(null);
    try {
      const result = await apiFetch<BatchStatusSaved>(`/api/plans/${plan.id}/status`, {
        method: "POST",
        body: JSON.stringify({ status_id: status }),
      });
      setИтог(result.changed ? `записано: «${result.status_name}»` : `партия уже «${result.status_name}»`);
      onSaved(result);
    } catch (error) {
      setБеда(error instanceof ApiFailure ? error.message : "areserv не ответила");
    } finally {
      setИдёт(false);
    }
  };

  return (
    <SidePanel
      title={`Партия ${plan.id} · статус`}
      width="w-[min(420px,94vw)]"
      portal
      annot="статус партии"
      onClose={onClose}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 text-[12.5px]">
        <div className="flex items-center gap-2">
          <span className="text-fg-muted">сейчас</span>
          <Badge tone={STATUS_TONE[plan.status_id]} text={plan.status_name} />
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="статус партии">
          {КНОПКИ.map((b) => {
            const сейчас = plan.status_id === b.status;
            return (
              <button
                key={b.status}
                type="button"
                disabled={сейчас || Boolean(причина) || идёт}
                title={сейчас ? "партия уже в этом статусе" : (причина ?? undefined)}
                onClick={() => void сменить(b.status)}
                className={
                  "h-7 rounded-[var(--radius-ui)] border px-2.5 " +
                  (сейчас
                    ? "border-accent bg-accent-dim text-fg"
                    : "border-border bg-bg text-fg hover:border-accent hover:text-accent") +
                  " disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:text-fg"
                }
              >
                {b.label}
                {сейчас ? " · сейчас" : ""}
              </button>
            );
          })}
        </div>
        {причина ? <div className="text-fg-muted">Статус не сменить: {причина}.</div> : null}
        {plan.is_paid ? (
          <div className="text-warn">QR-папка партии оплачена — проверьте, прежде чем отменять.</div>
        ) : null}
        <div className="text-fg-faint">
          Отмена гасит партию целиком; строки «в заказе» остаются как есть. Партию, где
          отменено всё, areserv сама не закроет — для этого и нужна «Отмена».
        </div>
        {беда ? <div className="text-err">{беда}</div> : null}
        {итог ? <div className="text-ok">{итог}</div> : null}
      </div>
    </SidePanel>
  );
}
